'use client';

/**
 * ComposerSubmitCard — summary + send action.
 *
 * Builds the intent-aware payload (RFQ / RFQ-edit / Semantic RFQ /
 * MCP), resolves the submit strategy (MCP via capability store;
 * others fixed), fires `api.getStrategyData`, surfaces result
 * inline. On success: invalidates the React Query caches the
 * legacy drawer also invalidates, clears the cart store.
 *
 * Parallel implementation of `cart-drawer.tsx::handleSubmit` +
 * `buildSubmitPayload`. Shares the cart-store data layer; does
 * not import from `components/cart/*`. The payload-shaping
 * duplication is intentional cohabitation cost — both paths
 * collapse to one when the legacy drawer retires.
 *
 * @see klyde_edge/composition/cards/composer.py — composer_submit spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Send, Loader2, CheckCircle, XCircle, FolderOpen } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import {
  useCartStore,
  type IntentType,
  getItemPrice,
} from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { api } from '@/lib/api/client';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useComposerLabel, useAffordance } from '@/lib/stores/total-matrix-store';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';

/**
 * Strategy lookup keys — `(category, action)` pairs the BFF
 * capability registry uses to expose live strategy names. Cards
 * reference strategies by *semantic identity* (StrategyRef
 * doctrine), not by literal name, so a BFF rename doesn't break
 * the consumer. The actual strategy name lives in the registry,
 * resolved at render time via `useCapabilityStore.strategyName()`.
 *
 * @see klyde_edge/ui_config/handlers/procurement.py — bff_strategy
 *      decorations carry `category="procurement", action="create_rfq"`
 *      / `"update_rfq"` / `"create_semantic_rfq"`
 * @see klyde_edge/klyde_intelligence/projects/handler.py —
 *      `category="cost_plans"`, action `"save"` / `"update"`
 */
const INTENT_LABEL_KEYS: Record<IntentType, { labelKey: string; submitKey: string }> = {
  rfq:      { labelKey: 'intent_rfq_label',      submitKey: 'submit_rfq' },
  rfq_edit: { labelKey: 'intent_rfq_edit_label',  submitKey: 'submit_rfq_edit' },
  po:       { labelKey: 'intent_po_label',         submitKey: 'submit_po' },
  mcp:      { labelKey: 'intent_mcp_label',        submitKey: 'submit_mcp' },
};

interface SubmitResult {
  success: boolean;
  message: string;
  reference?: string;
  // Set only for a successful mcp create/update — lets the result panel
  // offer "Open cost plan" instead of leaving the buyer to switch modes
  // and re-find what was just created by browsing Project mode.
  mcpNav?: { projectId: string; projectName: string; mcpSid: string; mcpName: string };
}

export function ComposerSubmitCard() {  const queryClient = useQueryClient();

  const items = useCartStore((s) => s.items);
  const activeIntent = useCartStore((s) => s.activeIntent);
  const projectId = useCartStore((s) => s.projectId);
  const projectName = useCartStore((s) => s.projectName);
  const isNewProject = useCartStore((s) => s.isNewProject);
  const vendorIds = useCartStore((s) => s.vendorIds);
  const requestedDate = useCartStore((s) => s.requestedDate);
  const rfqDeadline = useCartStore((s) => s.rfqDeadline);
  const tenderName = useCartStore((s) => s.tenderName);
  const notes = useCartStore((s) => s.notes);
  const editingMcpSid = useCartStore((s) => s.editingMcpSid);
  // Gates the Name/Revision inputs below — a light-append session is
  // targeting an EXISTING plan's own fixed identity, not minting a new
  // one, so renaming/re-revisioning it from this lightweight flow isn't
  // offered. Full-reload edit (editingMode 'full_replace'/unset with
  // editingMcpSid set) and new-plan creation keep the fields exactly as
  // they were — only light_append hides them.
  const editingMode = useCartStore((s) => s.editingMode);
  const clear = useCartStore((s) => s.clear);

  // Scene-only export — the review-and-submit summary (cart-store draft).
  useCardExport(useMemo(() => ({
    label: 'Submit',
    content_key: 'composer_submit',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: `${activeIntent ?? '—'}: ${items.length} items · ${vendorIds.length} vendors · ${projectName || 'no project'}`,
    json: {
      intent: activeIntent ?? null,
      item_count: items.length,
      vendor_count: vendorIds.length,
      project: projectName || null,
      is_new_project: isNewProject,
      response_deadline: rfqDeadline || null,
      delivery: requestedDate || null,
      tender_name: tenderName || null,
      has_notes: !!notes,
      editing_mcp_sid: editingMcpSid ?? null,
    },
  }), [activeIntent, items, vendorIds, projectName, isNewProject, rfqDeadline, requestedDate, tenderName, notes, editingMcpSid]));

  // Single capability lookup — the BFF dispatches per intent inside
  // `cart_submit`. Frontend stays intent-agnostic at the API layer.
  // Legacy per-intent strategies (rfq_create, mcp_save, …) still
  // exist for ki_ask agent calls; composer cart funnels all four
  // intents through this one dispatcher.
  const submitStrategy = useCapabilityStore((s) =>
    s.strategyName('procurement', 'cart_submit'),
  );
  // Resolved the same way costplan-lines-card.tsx / costplan-kpi-row-card.tsx
  // / synthesis-card.tsx look it up — needed so a successful MCP save/update
  // can invalidate the exact query key those cards read from.
  const costPlanDetailStrategy = useCapabilityStore((s) =>
    s.strategyName('cost_plans', 'detail'),
  );

  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const mcpPlanName = useCartStore((s) => s.mcpPlanName);
  const setMcpPlanName = useCartStore((s) => s.setMcpPlanName);

  // Same store calls the explorer's own project-row / McpRow click handlers
  // use (project-list-card.tsx) — replicating that exact sequence so
  // "Open cost plan" behaves identically to manually clicking through.
  const openTab = useTabStore((s) => s.openTab);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);
  const setMcpContext = useGraphSelectionStore((s) => s.setMcpContext);

  const handleOpenPlan = (nav: NonNullable<SubmitResult['mcpNav']>) => {
    // Order matters — openTab() clears graph-selection-store (including
    // projectId), so the project tab + its context must land before the
    // mcp tab + its context, exactly as project-list-card.tsx's own
    // handleOpenProject / McpRow onClick do it.
    openTab({ id: `project-${nav.projectId}`, type: 'project', title: nav.projectName, entityId: nav.projectId });
    setProjectContext(nav.projectId, { name: nav.projectName, rag_status: '', rag_label: '', rfq_count: 0, po_count: 0 });
    openTab({ id: `mcp-${nav.mcpSid}`, type: 'mcp', title: nav.mcpName, entityId: nav.mcpSid });
    setMcpContext(nav.mcpSid, nav.mcpName);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const intentKeys = INTENT_LABEL_KEYS[activeIntent];
  const intentLabel = useComposerLabel(intentKeys.labelKey);
  const intentSubmitLabel = useComposerLabel(intentKeys.submitKey);
  const mcpUpdateLabel = useComposerLabel('submit_mcp_update');
  const submitTestId = useAffordance('composer.submit.update');
  const mcpNameTestId = useAffordance('composer.submit.mcp_name');
  const creatingLabel = useComposerLabel('submit_creating');
  const updatingLabel = useComposerLabel('submit_updating');
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const estimatedTotal = items.reduce(
    (sum, item) => sum + (getItemPrice(item) || 0) * item.quantity,
    0,
  );

  // Dump the cart store as-is. No per-intent switching, no field
  // renaming — the BFF's `cart_submit` strategy (Pydantic
  // `CartSubmission`) is the contract. Adding a new field to the
  // cart store + the BFF schema lights up automatically; nothing
  // else changes here.
  const buildPayload = (): Record<string, unknown> => {
    const s = useCartStore.getState();
    return {
      intent: s.activeIntent,
      items: s.items.map((i) => ({
        // Wire field is `semantic_id` on the BFF side (cart_submit's
        // intent-agnostic payload). Sourced from the CartItem's
        // product_sid.
        semantic_id: i.product_sid,
        // Pass through as-is (CartItemPayload.line_id, mirrored into
        // McpCartLine by cart_submit's _mcp_body — mcp intent only,
        // other intents ignore it). Whether this is a real server-issued
        // LineItem cdk or a client-minted session UUID is not this
        // component's decision — the BFF validates and decides whether
        // to reuse it or mint a fresh line. Mirrors cart-drawer.tsx.
        line_id: i.line_id,
        name: i.name,
        quantity: i.quantity,
        sku: i.sku,
        image_url: i.image_url,
        vendor_id: i.vendor_id,
        vendor_name: i.vendor_name,
        list_price: i.list_price,
        vendor_cost: i.vendor_cost,
        spec: i.spec,
        target_date: i.target_date,
        room_placements: i.room_placements,
        source_line_sid: i.source_line_sid,
      })),
      project_sid: s.projectId,
      project_name: s.projectName,
      is_new_project: s.isNewProject,
      vendor_ids: s.vendorIds,
      vendor_emails: s.vendorEmails,
      requested_date: s.requestedDate,
      rfq_deadline: s.rfqDeadline,
      rfq_group: s.rfqGroup,
      tender_name: s.tenderName.trim() || null,
      notes: s.notes || null,
      revision: s.revision,
      editing_rfq_id: s.editingRfqId,
      editing_rfq_sid: s.editingRfqSid,
      editing_mcp_sid: s.editingMcpSid,
      // 'light_append' (targetMcpForAppend) → cart_submit dispatches
      // mcp_line_append per item; null/'full_replace' (loadMcpForEdit,
      // or no edit session) → today's full mcp_update replace, unchanged.
      editing_mode: s.editingMode,
      // Distinct from projectName — a plan isn't named after its
      // project. Empty → backend derives "{project} — {date}" at save
      // time (mirrors tenderName's pattern above).
      mcp_name: s.mcpPlanName || null,
      // Dev-mode actor identity — gateway injects from JWT in prod.
      // Keeping it on the payload covers the dev-user-store path.
      requestor_email: selectedUser?.email,
    };
  };

  const handleSubmit = async () => {
    if (items.length === 0) return;

    const isEditingMcp = activeIntent === 'mcp' && !!editingMcpSid;

    if (!submitStrategy) {
      setResult({
        success: false,
        message: `${intentLabel} strategy not registered in capability store`,
      });
      return;
    }
    if (activeIntent === 'po' && vendorIds.length === 0) {
      setResult({ success: false, message: 'Select at least one vendor for the Semantic RFQ' });
      return;
    }
    if (activeIntent === 'rfq_edit' && !useCartStore.getState().editingRfqId) {
      setResult({ success: false, message: 'No RFQ loaded for editing' });
      return;
    }
    if (activeIntent === 'mcp' && !isEditingMcp && (!projectId || !projectName)) {
      setResult({ success: false, message: 'Select a project for the Master Cost Plan' });
      return;
    }

    setIsSubmitting(true);
    setResult(null);

    try {
      const response = await api.getStrategyData<{
        reference?: string;
        semantic_id?: string;
        rfq_id?: number;
        message?: string;
        x_rfq_group_id?: string;
        rfqs?: Array<{ rfq_id: number; reference: string; vendor_id: string; sent: boolean }>;
      }>(submitStrategy, buildPayload());

      if (response.success) {
        const rfqs = response.data.rfqs;
        const isMultiVendorRfq =
          activeIntent === 'rfq' && Array.isArray(rfqs) && rfqs.length > 1;
        // MCP saves already carry a real reference (the plan's name), but
        // it isn't unique — several plans can share a project name. The
        // semantic_id is what you'd actually query with, so append it.
        const reference = isMultiVendorRfq
          ? `${rfqs!.length} RFQs · ${response.data.x_rfq_group_id ?? ''}`
          : activeIntent === 'mcp' && response.data.semantic_id
            ? `${response.data.reference ?? ''} (${response.data.semantic_id})`.trim()
            : (response.data.reference ??
              (response.data.rfq_id ? `ID: ${response.data.rfq_id}` : undefined));
        setResult({
          success: true,
          message:
            activeIntent === 'rfq_edit'
              ? response.data.message || 'RFQ updated'
              : isEditingMcp
                ? 'Cost plan updated'
                : isMultiVendorRfq
                  ? `Created ${rfqs!.length} RFQs (one per vendor)`
                  : `${intentLabel} created successfully`,
          reference,
          // Closes the "creation stops here" gap: without this, a buyer who
          // just built a cost plan from discovery/mode11 has no way back to
          // it except leaving to browse Project mode from scratch.
          mcpNav:
            activeIntent === 'mcp' && projectId && response.data.semantic_id
              ? {
                  projectId,
                  projectName: projectName || 'Project',
                  mcpSid: response.data.semantic_id,
                  mcpName: mcpPlanName || projectName || 'Cost Plan',
                }
              : undefined,
        });
        queryClient.invalidateQueries({ queryKey: ['project-subfolders'] });
        queryClient.invalidateQueries({ queryKey: ['project-explore'] });
        queryClient.invalidateQueries({ queryKey: ['obeya-projects'] });
        if (activeIntent === 'rfq' || activeIntent === 'rfq_edit') {
          queryClient.invalidateQueries({ queryKey: ['rfq-comparison'] });
          queryClient.invalidateQueries({ queryKey: ['strategy-data', 'rfi_details'] });
        }
        // Was missing — a saved mcp_update left costplan_details cached
        // stale for every card reading it (pivot table, KPI row, Plan
        // Synthesis), so a deleted line kept painting until something
        // else forced a refetch. Prefix match (no semantic_id in the
        // key) — cheap and correct even without knowing which cached
        // params array matches; only mounted queries actually refetch.
        if (activeIntent === 'mcp') {
          queryClient.invalidateQueries({ queryKey: ['strategy-data', costPlanDetailStrategy] });
        }
        clear();
      } else {
        setResult({
          success: false,
          message:
            response.error ||
            `Failed to ${isEditingMcp ? 'update' : 'create'} ${intentLabel}`,
        });
      }
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLabel =
    editingMcpSid && activeIntent === 'mcp' ? mcpUpdateLabel : intentSubmitLabel;
  const inFlightLabel =
    editingMcpSid && activeIntent === 'mcp' ? updatingLabel : creatingLabel;

  return (
    <RailCard title="Submit" icon={Send}>
      <div className="flex items-center justify-between mb-2">
        <span className={text.muted}>
          {items.length} product{items.length !== 1 ? 's' : ''} ({totalItems} units)
        </span>
        {estimatedTotal > 0 && (
          <span className={text.value}>${estimatedTotal.toLocaleString()}</span>
        )}
      </div>

      {/* Cost Plan name — DISTINCT from the project name shown in the
          Project card above. Deliberately not defaulted to projectName
          client-side (that was the bug — every plan under a project
          ended up sharing its name). Empty → backend derives
          "{project} — {date}" at save time. */}
      {activeIntent === 'mcp' && editingMode !== 'light_append' && (
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor="composer-mcp-name" className={cn(text.muted, 'text-muted-foreground')}>
            Name
          </label>
          <input
            id="composer-mcp-name"
            data-testid={mcpNameTestId}
            type="text"
            value={mcpPlanName}
            onChange={(e) => setMcpPlanName(e.target.value)}
            placeholder={projectName ? `${projectName} — ${new Date().toLocaleDateString()}` : 'Cost Plan name'}
            className={cn(
              text.body,
              'flex-1 h-6 px-1.5 rounded border border-border bg-background',
              'focus:outline-none focus:ring-1 focus:ring-primary',
            )}
          />
        </div>
      )}

      {/* The old Composer "Revision" text field (McpCartBody.revision) is
          retired — it was a cosmetic, PDF-only label with no connection
          to the plan's real versioning system (baseline/scenario,
          queries/rev.py), and surfacing it next to real plan identity
          invited exactly that confusion. Not wiring to the real rev
          system yet either — removed outright rather than carried
          forward half-connected. */}

      <button
        data-testid={submitTestId}
        onClick={handleSubmit}
        disabled={isSubmitting || items.length === 0}
        className={cn(
          text.body,
          'w-full font-medium px-3 py-2 rounded-md flex items-center justify-center gap-2 transition-colors',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {inFlightLabel}
          </>
        ) : (
          <>
            <Send className="h-3.5 w-3.5" />
            {submitLabel}
          </>
        )}
      </button>

      {/* Download removed from Composer (was wired to the deprecated
          mcp_document/Jinja2-WeasyPrint path, not the current Typst/
          Excel exports). Export belongs to viewing/managing an
          already-saved plan — mode1's rail already owns that, including
          the newer formats — not to staging-and-submitting here. */}

      {result && (
        <div
          className={cn(
            'mt-2 rounded-md p-2 flex items-start gap-2',
            result.success
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : 'bg-destructive/10 text-destructive',
          )}
        >
          {result.success ? (
            <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          ) : (
            <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className={text.body}>{result.message}</p>
            {result.reference && (
              <p className={cn(text.muted, 'mt-0.5')}>Reference: {result.reference}</p>
            )}
            {result.mcpNav && (
              <button
                type="button"
                onClick={() => handleOpenPlan(result.mcpNav!)}
                className={cn(
                  text.body,
                  'mt-1.5 font-medium inline-flex items-center gap-1.5 text-green-700 dark:text-green-400 hover:underline',
                )}
              >
                <FolderOpen className="h-3.5 w-3.5" />
                Open cost plan
              </button>
            )}
          </div>
        </div>
      )}
    </RailCard>
  );
}
